import { clockTime, meridiem } from './attendance'

/**
 * Declaring an absence, and arranging cover for it (D-328, D-330).
 *
 * Cover used to be inferred backwards from whoever took the register, so the
 * two states that matter most had nowhere to live: *out and nobody asked*, and
 * *asked and not yet answered*.
 */
/**
 * **Hand-written, and bound to the C# enum by a test** (`CoverStateParityTests`).
 *
 * The switch below says adding a state is a compile error here — which is only
 * true if this union is complete, and nothing on this side can know that.
 * `AwaitingApproval` (D-540) was added server-side and `tsc -b` stayed green
 * while Tonya's queue rendered the row with no sentence on it at all.
 */
export type CoverState = 'Uncovered' | 'Asked' | 'Accepted' | 'AwaitingApproval' | 'Withdrawn'

export type CoverRow = {
  id: string
  occurrenceId: string
  date: string
  startTime: string | null
  className: string
  absentName: string

  /**
   * Who is out, as an id.
   *
   * **`/cover/colleagues` removes the caller, not the absent teacher** — right
   * on her own panel, where those are the same person, and wrong on Tonya's
   * queue, where it left the teacher who is out offered as her own coverer on
   * every row. Filtering needs an id: the studio has two Ashleys (D-82).
   */
  absentStaffId: string

  askedStaffId: string | null
  askedName: string | null
  note: string | null
  declineReason: string | null

  /**
   * Arrives as a name, not a number — the API serialises enums as names (D-32),
   * and typing this to a number is the trap the endpoint tests hit first.
   */
  state: CoverState

  /**
   * This one is waiting on *you*.
   *
   * Answered by the server, not compared here: the alternative was putting a
   * staff id in the session so a screen could match two guids.
   */
  askedOfMe: boolean

  /**
   * You are the one who is out.
   *
   * **A second flag, because one was answering two questions.** Splitting a
   * list on `askedOfMe` alone put a row back among the ones she is watching
   * the instant she accepted — so it told her by name, in the third person,
   * that she was covering, and offered her a withdraw she is refused.
   */
  iAmAbsent: boolean

  /**
   * The session itself has been called off (D-355).
   *
   * On the row rather than worked out here, because the row is where the
   * decision gets made: cancelling is the fallback after the sub search fails,
   * so the control sits beside *ask somebody else* and has to know whether it
   * has already been taken.
   */
  isCancelled: boolean

  /**
   * A teacher called it off, so a teacher may put it back.
   *
   * A studio closure is the studio's to withdraw, and offering an undo the
   * server refuses would be a control that only ever refuses.
   */
  cancelledByMe: boolean
}

/**
 * Somebody who may stand in. With a day asked for, `busyFor` names the
 * caller's sessions that day she already has something against (D-597), so a
 * row offers only who is free - the server does the time maths (D-81).
 */
/**
 * Somebody who could stand in.
 *
 * **`busyFor` and `away` are different strengths and must not be flattened.**
 * Busy is a clash the studio can see - she teaches something else at that hour -
 * and it is per session. Away is a statement she made about the whole day, and
 * it is the only one the ask actually refuses (D-681). A picker that treated
 * them alike would either offer a name the save rejects or hide somebody who is
 * perfectly askable.
 *
 * **`outsideHoursFor` is the softest of the three** (D-708): the sessions she
 * is outside her usual hours for, on a day she has nothing of her own at the
 * studio. A mark only - she stays in the list and the ask accepts her.
 */
export type Colleague = {
  id: string
  name: string
  busyFor?: string[]
  away?: boolean
  outsideHoursFor?: string[]
}

async function json<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init)

  if (!res.ok) {
    /*
     * The server already wrote the useful sentence — "That session has already
     * started", "You have already said you are out for this one" — so repeating
     * it here in different words would give one condition two names.
     */
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? `Something went wrong (${res.status})`)
  }

  return res.status === 204 ? (undefined as T) : res.json()
}

/** Hers: what she has declared, and what she has been asked to cover. */
export const fetchMyCover = () => json<CoverRow[]>('/api/cover/mine')

/** Everything still waiting on somebody. Owner-only. */
export const fetchUncovered = () => json<CoverRow[]>('/api/cover/uncovered')

/**
 * A night nobody has yet, offered to anybody who could take it (D-650).
 *
 * **Not a `CoverRow`, and the missing field is the point.** A cover row carries
 * `declineReason` — the words the last colleague wrote when she said no — which
 * exists so the teacher who is out is not left guessing. This list is read by
 * every teacher in the studio, and that sentence should not travel that far
 * (D-355 draws the same line for a cancellation reason).
 */
export type OpenCoverRow = {
  id: string
  occurrenceId: string
  date: string
  startTime: string | null

  /**
   * When it finishes, or null where the class records none (D-776).
   *
   * **The question that prompted it**, from a teacher reading this board:
   * *"I don't know how long the class is. I teach my own class at 7, and I know
   * her class is 6:15... till?"* The server had the answer in hand - it selects
   * the end to work out `clashes` - and did not put it on the row.
   */
  endTime: string | null

  className: string
  absentName: string

  /** What whoever picks this class up should know (D-364). */
  note: string | null

  /**
   * She already has something on at that hour (D-650).
   *
   * Worked out by the server, half-open, against her classes *and* her room
   * bookings — the same maths `/cover/colleagues` does for the dropdown
   * (D-597), so the two screens cannot disagree about who is free.
   */
  clashes: boolean
}

/** The board: only sessions nobody is currently being asked about. */
export const fetchOpenCover = () => json<OpenCoverRow[]>('/api/cover/open')

/** One thing already in her evening (D-776). */
export type CoverEveningRow = {
  start: string
  end: string

  /**
   * Whether that end is recorded or assumed.
   *
   * A class with no end time is an hour long to the overlap test, because it
   * has to compare something — and unknown to a reader. Printing the assumed
   * hour would put a finishing time on screen that the studio never set.
   */
  endKnown: boolean

  label: string

  /** A room she holds rather than a class she is in (D-80). */
  isRoomBooking: boolean

  /** This row is the class being offered, laid into her own evening. */
  isOffered: boolean

  /** It runs into the offered class. Never true on the offered row itself. */
  overlaps: boolean
}

/**
 * A night she is being offered, with her own evening around it (D-776).
 *
 * **The same list the clash test walks**, rather than a second one assembled
 * from the same tables — so the sheet cannot show a clear evening above a
 * control saying she is busy.
 */
export type CoverEvening = {
  id: string
  occurrenceId: string
  date: string
  startTime: string | null
  endTime: string | null
  className: string
  roomName: string | null
  absentName: string
  note: string | null

  /** How many dancers she would be walking into. */
  enrolled: number

  clashes: boolean
  evening: CoverEveningRow[]
}

export const fetchCoverEvening = (id: string) =>
  json<CoverEvening>(`/api/cover/${encodeURIComponent(id)}/evening`)

/**
 * When a cover row is, as a short line: *Thu 12 Nov &middot; 5:30pm*.
 *
 * **One function, because two panels print it.** Her own cover section and the
 * open board had character-identical copies within a day of each other, which
 * is the drift D-199 names — the second copy never receives the first's fixes,
 * and a date format is exactly the kind of thing that gets fixed once.
 *
 * A class with no start time meets on no dates (D-84), so the time is optional
 * rather than assumed.
 */
export function coverWhen(row: {
  date: string
  startTime: string | null
  /**
   * When it finishes (D-776). Optional, because her own cover panel has no end
   * to print: a `CoverRow` is about an absence she declared, where the class is
   * already hers and the question was never *how long*.
   */
  endTime?: string | null
}): string {
  const date = new Date(`${row.date}T00:00:00`)
  const day = date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })

  if (!row.startTime) return day

  return `${day} · ${timeRange(row.startTime, row.endTime ?? null)}`
}

/**
 * *6:15&ndash;7:15pm*, or *11:30am&ndash;12:15pm* when they straddle noon (D-776).
 *
 * <b>The meridiem is printed once where both halves share it.</b> "6:15pm-7:15pm"
 * is the same fact said twice on a row that is already carrying a 69-character
 * Jackrabbit name (D-115), and this line is read on a phone.
 *
 * <b>No end means no range.</b> A class with no end time recorded is left as a
 * start, because the alternative is printing the hour the clash test assumes -
 * a finishing time the studio never said, on the row where somebody is deciding
 * whether her evening holds.
 */
export function timeRange(start: string, end: string | null): string {
  const from = `${clockTime(start)}${meridiem(start)}`

  if (!end) return from

  return meridiem(start) === meridiem(end)
    ? `${clockTime(start)}–${clockTime(end)}${meridiem(end)}`
    : `${from}–${clockTime(end)}${meridiem(end)}`
}

/**
 * *"I will take that one."*
 *
 * Raises a cover ask against herself and answers it in the same request, so a
 * claim goes through every rule a colleague's ask already obeys — including the
 * studio's say-so when she is a student teacher from outside the class (D-540),
 * which is why the state that comes back is not always `Accepted`.
 */
export const claimCover = (id: string) => post(`/api/cover/${id}/claim`)

/**
 * Who may stand in.
 *
 * **`date` narrows per session; `absenceId` narrows per day.** Two of the three
 * screens that use this hold only an absence - they are re-pointing one cover
 * request rather than planning an evening - so without the second argument they
 * could not narrow at all, and would offer names the ask then refuses (D-682).
 * The server resolves the absence to its date, because a caller that has to
 * work that out is a caller that can get it wrong.
 */
export const fetchColleagues = (date?: string, absenceId?: string) => {
  const query = new URLSearchParams()

  if (date) query.set('date', date)
  if (absenceId) query.set('absenceId', absenceId)

  const qs = query.toString()

  return json<Colleague[]>(qs ? `/api/cover/colleagues?${qs}` : '/api/cover/colleagues')
}

const post = (url: string, body?: unknown) =>
  json<{ id: string; state: CoverState }>(url, {
    method: 'POST',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  })

/**
 * One session, rather than a whole day.
 *
 * **No caller in the SPA since D-354** replaced the single-session dialog with
 * the day grid. Kept deliberately: it is the narrower operation, and the day
 * endpoint is a loop around the same rules rather than a replacement for them.
 * Recorded here so the next reader finds a decision rather than a loose end —
 * an export nothing calls is how D-332 started.
 */
export const declareOut = (occurrenceId: string, askStaffId: string | null, note: string | null) =>
  post('/api/cover', { occurrenceId, askStaffId, note })

export const withdrawCover = (id: string) => post(`/api/cover/${id}/withdraw`)

/**
 * The studio's say-so on a student teacher from outside the class (D-540).
 *
 * **Only the owner reaches these** — they are gated on `ManageClasses`, unlike
 * every other route here, because the whole reason the state exists is that
 * the teacher who picked her may not wave it through.
 */

/**
 * Call the class off, when nobody could take it (D-355).
 *
 * Keyed on the occurrence rather than the cover request, because the class is
 * the thing being cancelled — the request is only how she got here, and a
 * studio closure reaches the same session with no request at all.
 */
/** What a cancel is about to do, or what it did (D-356). */
export type CancelPreview = {
  id: string
  status: string

  /** How many adults would hear, or did. */
  families: number
  applied: boolean
  /**
   * How many families the telling was handed off for (D-869) — **not** how many
   * were told, which is not knowable when the response is written.
   *
   * This was `emailed` and `notified`, filled by holding the request open for
   * the whole fan-out — eighteen seconds in production, a hundred and
   * forty-four on QA. Nothing in the app read either of them, so no screen
   * changed; the bell (`notice.familiesTold`) is what reports the result now.
   * Zero on a repeat tap, because nobody is told twice.
   */
  telling: number
}

/**
 * **`apply` is required, and false previews.**
 *
 * This is the only action in the app that reaches sixty households at once and
 * cannot be taken back, so the count comes back before anything is sent and a
 * request that does not say `apply` can only ever be harmless.
 */
export const cancelSession = (
  occurrenceId: string,
  reason: string,
  apply: boolean,
  /** What the families are told about why, or blank to tell them nothing (D-659). */
  familyNote: string | null = null,
) =>
  json<CancelPreview>(`/api/cover/occurrence/${occurrenceId}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason, apply, familyNote }),
  })

/** Put it back on. Restores the class and nothing else (D-355). */
export const reinstateSession = (occurrenceId: string) =>
  json<{ id: string; status: string }>(`/api/cover/occurrence/${occurrenceId}/reinstate`, {
    method: 'POST',
  })

/**
 * What this row is waiting for, in a sentence.
 *
 * **One function, because three screens ask it** — her own list, the answer
 * she is being asked for, and Tonya's queue. Two implementations of "what does
 * this state mean" drift, and drift here is a screen confidently naming the
 * wrong thing (D-261).
 */
export function coverSummary(row: CoverRow, mine: boolean): string {
  switch (row.state) {
    case 'Uncovered':
      return row.declineReason
        ? 'Nobody covering yet — the last person asked said no.'
        : 'Nobody covering yet.'

    case 'Asked':
      return mine
        ? `Waiting on ${row.askedName ?? 'an answer'}.`
        : 'Waiting on your answer.'

    case 'Accepted':
      return mine ? `${row.askedName ?? 'Somebody'} is covering.` : 'You are covering this one.'

    /*
     * She has said yes and the studio has not answered (D-540). Said as
     * *waiting*, never as covered: the night still has nobody, and a row that
     * read "is covering" and then stopped would be worse than one that never
     * claimed it.
     */
    case 'AwaitingApproval':
      return mine
        ? `${row.askedName ?? 'Somebody'} has said yes — waiting for the studio to agree.`
        : 'You have said yes — waiting for the studio to agree.'

    /*
     * No default: the switch is exhaustive over `CoverState`, so adding a
     * state is a compile error here rather than a row that renders nothing.
     * That holds only because the union above is checked against the C# enum;
     * on its own this comment was false for a whole commit.
     */

    case 'Withdrawn':
      return 'No longer out.'
  }
}

/**
 * One day of hers, as the grid reads it (D-354).
 *
 * **`alreadyHandled` is one word across both endpoints and both personas** —
 * the parent's bulk absence says the same thing on its own rows. It carries any
 * reason the row cannot be ticked, not only "already".
 */
export type DaySession = {
  occurrenceId: string
  className: string
  date: string
  startTime: string | null
  roomName: string | null
  enrolled: number

  /** Why the row cannot be *declared*, in words, or null. */
  alreadyHandled: string | null

  /**
   * Her live declaration for this session, and what it is waiting for (D-365).
   *
   * **This is what lets the dialog be the place she fixes a day, not only the
   * place she declares one.** Without it a declared row could be struck through
   * and nothing more, so re-asking after a decline had to happen on a separate
   * panel below her week — the "two very disconnected things" the feature
   * exists to remove.
   */
  coverRequestId: string | null
  coverState: CoverState | null

  /** Who she asked, while the state is `Asked`. */
  askedName: string | null

  /**
   * Why the last person said no.
   *
   * **Present without a name, and that is deliberate.** It is the reason on
   * the last no the envelope holds for this absence (D-552), and the row
   * reads *the session still needs somebody* rather than *who refused*
   * (D-328) — so the screen says the last person asked said no, never who.
   * Declined, not the decliner.
   */
  declineReason: string | null

  isCancelled: boolean

  /** A teacher called it off, so a teacher may put it back (D-355). */
  cancelledByMe: boolean
}

export type DeclaredDaySession = {
  occurrenceId: string
  className: string
  alreadyHandled: string | null
  /**
   * What was written for the row (D-630): the absence, and the cover ask if
   * a colleague was asked. A conversation that opened this door pins the
   * absence, which follows whoever is asked now. Null on a refused row.
   */
  absenceId: string | null
  askId: string | null
}

export type DeclaredDayResult = {
  sessions: DeclaredDaySession[]
  declared: number
  applied: boolean
  /** How many rows are being called off. */
  cancelling: number
  /**
   * How many people would hear, counted **once each** across every cancelled
   * row (D-362).
   *
   * **The server computes it because the client cannot.** `CancelPreview`
   * returns a number and no ids, so summing one preview per class
   * double-counts a parent with children in two of them — and this is the
   * figure the confirmation shows her before anything reaches sixty households
   * (D-356).
   */
  families: number
}

/**
 * Her classes on one named date, however far off.
 *
 * `/api/teacher` caps at a fortnight, which is right for a home screen and made
 * a Monday in October unreachable for somebody saying she is out.
 */
export const fetchTeacherDay = (date: string) =>
  json<DaySession[]>(`/api/teacher/day?date=${encodeURIComponent(date)}`)

/**
 * One ticked row and the one thing she wants done with it (D-361).
 *
 * **Mutually exclusive by construction, because they are exclusive in the
 * domain**: cancelling withdraws every live cover request for the session
 * (D-355), and `/accept` refuses a cancelled one. Two independent flags would
 * let this express a state the server cannot hold, and it would discard one of
 * them silently.
 */
export type DayChoice = {
  occurrenceId: string
  /**
   * Ask this colleague to take it.
   *
   * **Explicitly null when nobody is named, never omitted.** The endpoint reads
   * a row and "not asked" is a value rather than something it should infer from
   * an absent key — and a key that disappears is one a test cannot assert.
   */
  askStaffId: string | null
  /** Call it off instead of finding cover. */
  cancel: boolean

  /**
   * What whoever takes *this* class should know (D-375).
   *
   * **Per row, because a substitute takes one class and not a day.** It was a
   * single field on the whole submission, so a Tuesday with five classes sent
   * the same sentence to the person taking ballet and the person taking tap.
   *
   * Null on a cancellation — nobody is picking that class up, and the reason
   * for calling it off goes to Tonya through a separate field (D-364).
   */
  note: string | null
}

/**
 * Declare a whole day in one pass, or preview it.
 *
 * **Preview and apply from one flag**, so a malformed request can only ever be
 * harmless. A row with nobody named and no cancel is declared *uncovered*,
 * which is what puts it in Tonya's queue.
 *
 * **`note` and `reason` go to different people** (D-364). What the substitute
 * reads arrives in `cover.asked` as `coverNote`; the reason is Tonya's, is
 * never sent to a family, and is required only once a row says `cancel`.
 *
 * **The `note` parameter here is the *submission-level* one, and the SPA no
 * longer sends it** (D-375). A substitute takes one class, not a day, so the
 * note that reaches her is `DayChoice.note` on the row — this one survives
 * only as the server's fallback for a caller that predates the change, and the
 * dialog passes `null`. **Do not reach for it when adding a caller**: filling
 * it sends one sentence to everybody, which is the bug D-375 fixed. Kept
 * rather than deleted for the same reason `declareOut` above is kept, and
 * named here so the next reader finds a decision instead of a loose end.
 */
export const declareDayOut = (
  date: string,
  rows: DayChoice[],
  note: string | null,
  reason: string | null,
  apply: boolean,
  /** Remind whoever is asked if they have not answered (D-554). On unless she says otherwise. */
  nudges = true,
  /** What the families are told about why, or blank to tell them nothing (D-659). */
  familyNote: string | null = null,
) =>
  json<DeclaredDayResult>('/api/cover/day', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ date, rows, note, reason, apply, nudges, familyNote }),
  })

/**
 * Whether to offer her a way to say she is out (D-396).
 *
 * **Owner:** *"I am out shouldn't be on tonyas view."* It was keyed on
 * `BookRooms`, which she holds along with every other capability.
 *
 * **`teaches` alone does not separate her, and that was checked rather than
 * assumed.** `defaultTeacher` records the same trap one file over: her owner
 * account is created with `UserAccount.ForStaff`, and `observed` on production
 * `teaches` is **true** for her, because she has covered a session inside the
 * lookback window. In the dev database she teaches nothing, so a guard written
 * against `teaches` looks right locally and is wrong live.
 *
 * **So: owning a class, or having days without being the person whose view is
 * the whole studio.** `ViewAllSchedules` is that second signal, the same one
 * `defaultTeacher` uses and for the same reason — it is what sends a reader to
 * the timetable rather than to a portal.
 *
 * **The `ownsClasses` half is what stops this costing a senior teacher.**
 * Copying `defaultTeacher` outright would take the control away from anyone
 * granted `ViewAllSchedules`, and unlike a pre-filled name this has no other
 * route: the only other way into the dialog is a cover row she may not have.
 * A teacher with classes of her own keeps it however much else she is trusted
 * with.
 */
export function canDeclareOut(
  session:
    | { teaches?: boolean; ownsClasses?: boolean; capabilities?: string[] }
    | null
    | undefined,
): boolean {
  if (!session) return false
  if (session.ownsClasses === true) return true

  return session.teaches === true && !session.capabilities?.includes('ViewAllSchedules')
}

/**
 * Take a wrongly-named substitute off a night (D-492).
 *
 * Cover is inferred from taking a register (D-121), and until D-491 that
 * inference named anyone with a staff link — so somebody who teaches nothing
 * could end up recorded on a class she does not teach. This removes one
 * already written; nothing else can, because every other route to clearing
 * the field is guarded against a session that has already started.
 *
 * Refused with a 409 when somebody actually accepted the cover: she is
 * expecting to teach, and *withdraw* is the path for changing that.
 */
export async function clearCover(occurrenceId: string): Promise<void> {
  const res = await fetch(`/api/cover/occurrence/${occurrenceId}/clear-cover`, { method: 'POST' })

  if (res.status === 409) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? 'Somebody accepted this one — withdraw the cover instead.')
  }

  if (!res.ok) throw new Error(`Could not remove the substitute (${res.status})`)
}
